import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import DataGridViewport from "../components/DataGridViewport";
import LocalSelector from "./components/LocalSelector";
import { useUserContext } from "../hooks/UserContext";
import { useProveedores } from "../hooks/ProveedoresContext";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import { ProductoCatalogoItem } from "../services/productoService";
import { CompraLinea, CompraProducto, fetchGetProductosCompra, fetchGuardarCompra } from "../services/compraService";
import { isAbortError } from "../services/fetchService";
import { useNotification } from "../hooks/NotificationContext";

const today = () => new Date().toISOString().slice(0, 10);
const money = (value: number) => CurrencyFormatter.format(Number(value || 0));
const ivaOptions = [0, 10.5, 21];

const CompraCarga = () => {
  const { user } = useUserContext();
  const { showNotification } = useNotification();
  const { proveedores, loading: loadingProveedores, refreshProveedores } = useProveedores();
  const [idLocal, setIdLocal] = useState<string | undefined>();
  const [proveedor, setProveedor] = useState<ProductoCatalogoItem | null>(null);
  const [search, setSearch] = useState("");
  const [searchToApply, setSearchToApply] = useState("");
  const [productos, setProductos] = useState<CompraProducto[]>([]);
  const [totalProductos, setTotalProductos] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detalle, setDetalle] = useState<CompraLinea[]>([]);
  const [tipoComprobante, setTipoComprobante] = useState(1);
  const [letra, setLetra] = useState("A");
  const [facturaPv, setFacturaPv] = useState("");
  const [facturaNumero, setFacturaNumero] = useState("");
  const [remito, setRemito] = useState("");
  const [fecha, setFecha] = useState(today());
  const [fechaIva, setFechaIva] = useState(today());
  const [vencimiento, setVencimiento] = useState(today());
  const [noGravado, setNoGravado] = useState(0);
  const [iibb, setIibb] = useState(0);
  const [tem, setTem] = useState(0);
  const [percepcionIva, setPercepcionIva] = useState(0);
  const [impuestosInternos, setImpuestosInternos] = useState(0);
  const [bonificacion, setBonificacion] = useState(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPaginationModel((current) => ({ ...current, page: 0 }));
      setSearchToApply(search);
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (!user || !idLocal) {
      setProductos([]);
      setTotalProductos(0);
      return;
    }

    const controller = new AbortController();
    setLoadingProductos(true);
    fetchGetProductosCompra(idLocal, user, paginationModel.page, paginationModel.pageSize, {
      search: searchToApply,
      idProveedor: proveedor?.id,
    }, controller.signal)
      .then((data) => {
        setProductos(data.items);
        setTotalProductos(data.total);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        setProductos([]);
        setTotalProductos(0);
        showNotification("No se pudieron cargar los productos de compra.", "error");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingProductos(false);
      });

    return () => controller.abort();
  }, [idLocal, paginationModel.page, paginationModel.pageSize, proveedor?.id, searchToApply, showNotification, user]);

  const subtotal = useMemo(() => detalle.reduce((acc, item) => acc + item.unitarioSinIva * item.cantidad, 0), [detalle]);
  const iva21 = useMemo(() => detalle.reduce((acc, item) => acc + (item.unitarioSinIva * item.cantidad * item.ivaCompra / 100), 0), [detalle]);
  const totalLineas = useMemo(() => detalle.reduce((acc, item) => acc + item.totalConIva, 0), [detalle]);
  const totalFactura = useMemo(
    () => totalLineas + noGravado + iibb + tem + percepcionIva + impuestosInternos - bonificacion,
    [bonificacion, iibb, impuestosInternos, noGravado, percepcionIva, tem, totalLineas]
  );

  const refrescarProveedores = () => {
    refreshProveedores()
      .then(() => showNotification("Proveedores actualizados.", "success"))
      .catch(() => showNotification("No se pudieron actualizar los proveedores.", "error"));
  };

  const agregarProducto = (producto: CompraProducto) => {
    setDetalle((actual) => {
      const existente = actual.find((item) => item.idProducto === producto.idProducto);
      if (existente) {
        return actual.map((item) => item.idProducto === producto.idProducto ? recalcularLinea({ ...item, cantidad: item.cantidad + 1 }) : item);
      }

      return [...actual, recalcularLinea({
        idProducto: producto.idProducto,
        producto: producto.producto,
        cantidad: 1,
        ivaCompra: Number(producto.ivaCompra || 0),
        unitarioSinIva: Number(producto.precioCompraBruto || producto.precioCompra || 0),
        unitarioConIva: Number(producto.precioCompra || 0),
        totalConIva: Number(producto.precioCompra || 0),
      })];
    });
  };

  const actualizarLinea = (idProducto: number, field: "cantidad" | "ivaCompra" | "unitarioSinIva" | "unitarioConIva", value: number) => {
    setDetalle((actual) => actual.map((item) => item.idProducto === idProducto ? recalcularLinea({ ...item, [field]: value }) : item));
  };

  const eliminarLinea = (idProducto: number) => {
    setDetalle((actual) => actual.filter((item) => item.idProducto !== idProducto));
  };

  const guardar = () => {
    if (!user || !idLocal || !proveedor) {
      showNotification("Seleccione local y proveedor.", "warning");
      return;
    }
    if (!facturaPv.trim() || !facturaNumero.trim()) {
      showNotification("Complete los datos de factura.", "warning");
      return;
    }
    if (detalle.length === 0) {
      showNotification("Agregue productos a la compra.", "warning");
      return;
    }

    setSaving(true);
    fetchGuardarCompra(user, {
      tipoComprobante,
      idLocal: Number(idLocal),
      puntoVenta: Number((user as any).pv ?? (user as any).PV ?? 0),
      idProveedor: proveedor.id,
      proveedor: proveedor.nombre,
      numeroRemito: remito,
      numeroFacturaPv: facturaPv,
      numeroFactura: facturaNumero,
      letraFactura: letra,
      fecha,
      fechaIva,
      noGravado,
      subtotal,
      iva21,
      iva105: 0,
      iva27: 0,
      exento: 0,
      ingresosBrutos: iibb,
      impuestosInternos,
      tem,
      percepcionIva,
      bonificacion,
      totalFactura,
      formaPago: 6,
      vencimiento,
      detalle,
      vencimientos: [{ fecha: vencimiento, importe: totalFactura }],
    })
      .then((result) => {
        showNotification(result.message ?? result.Message ?? "Compra guardada correctamente.", "success");
        setDetalle([]);
        setFacturaNumero("");
        setFacturaPv("");
        setRemito("");
        setPaginationModel((current) => ({ ...current }));
      })
      .catch((error) => showNotification(error?.message ?? "No se pudo guardar la compra.", "error"))
      .finally(() => setSaving(false));
  };

  const productColumns = useMemo<GridColDef[]>(() => [
    { field: "codigo", headerName: "Codigo", minWidth: 130, flex: 0.6 },
    { field: "producto", headerName: "Producto", minWidth: 260, flex: 1.4 },
    { field: "stock", headerName: "Stock", minWidth: 110, type: "number", flex: 0.5 },
    { field: "precioCompra", headerName: "Costo", minWidth: 130, flex: 0.6, valueFormatter: ({ value }) => money(Number(value)) },
    { field: "ivaCompra", headerName: "IVA compra", minWidth: 120, type: "number", flex: 0.5 },
  ], []);

  const detalleColumns = useMemo<GridColDef[]>(() => [
    { field: "producto", headerName: "Producto", minWidth: 260, flex: 1.3 },
    {
      field: "cantidad",
      headerName: "Cantidad",
      minWidth: 130,
      renderCell: (params) => (
        <TextField type="number" size="small" value={params.row.cantidad} inputProps={{ min: 0, step: "0.001" }} onChange={(event) => actualizarLinea(params.row.idProducto, "cantidad", Number(event.target.value))} />
      ),
    },
    {
      field: "unitarioSinIva",
      headerName: "Unit. s/IVA",
      minWidth: 145,
      renderCell: (params) => (
        <TextField type="number" size="small" value={params.row.unitarioSinIva} inputProps={{ min: 0, step: "0.01" }} onChange={(event) => actualizarLinea(params.row.idProducto, "unitarioSinIva", Number(event.target.value))} />
      ),
    },
    {
      field: "ivaCompra",
      headerName: "IVA %",
      minWidth: 115,
      renderCell: (params) => (
        <TextField
          select
          size="small"
          value={params.row.ivaCompra}
          onChange={(event) => actualizarLinea(params.row.idProducto, "ivaCompra", Number(event.target.value))}
          fullWidth
        >
          {ivaOptions.map((iva) => (
            <MenuItem key={iva} value={iva}>
              {iva} %
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      field: "unitarioConIva",
      headerName: "Unit. c/IVA",
      minWidth: 145,
      renderCell: (params) => (
        <TextField type="number" size="small" value={params.row.unitarioConIva} inputProps={{ min: 0, step: "0.01", readOnly: true }} />
      ),
    },
    { field: "totalConIva", headerName: "Total", minWidth: 130, valueFormatter: ({ value }) => money(Number(value)) },
    {
      field: "acciones",
      headerName: "",
      minWidth: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => <IconButton size="small" onClick={() => eliminarLinea(params.row.idProducto)}><DeleteIcon fontSize="small" /></IconButton>,
    },
  ], []);

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <AddShoppingCartIcon color="primary" />
        <Typography variant="h4">Carga de Compras</Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}><LocalSelector onLocalChange={setIdLocal} /></Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete options={proveedores} value={proveedor} loading={loadingProveedores} getOptionLabel={(option) => option.nombre} onChange={(_, value) => setProveedor(value)} renderInput={(params) => <TextField {...params} label="Proveedor" />} />
          </Grid>
          <Grid item xs={12} md={1}>
            <Tooltip title="Refrescar proveedores">
              <span>
                <Button
                  variant="outlined"
                  onClick={refrescarProveedores}
                  disabled={loadingProveedores}
                  fullWidth
                  sx={{ height: 56, minWidth: 56, px: 0 }}
                >
                  {loadingProveedores ? <CircularProgress size={18} /> : <RefreshIcon />}
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={6} md={2}><TextField select label="Comprobante" value={tipoComprobante} onChange={(e) => setTipoComprobante(Number(e.target.value))} fullWidth><MenuItem value={1}>Compra</MenuItem><MenuItem value={2}>Nota de credito</MenuItem></TextField></Grid>
          <Grid item xs={6} md={2}><TextField label="Forma de pago" value="Cuenta corriente" fullWidth InputProps={{ readOnly: true }} /></Grid>
          <Grid item xs={4} md={1}><TextField label="Letra" value={letra} onChange={(e) => setLetra(e.target.value.toUpperCase())} fullWidth /></Grid>
          <Grid item xs={4} md={2}><TextField label="PV factura" value={facturaPv} onChange={(e) => setFacturaPv(e.target.value)} fullWidth /></Grid>
          <Grid item xs={4} md={2}><TextField label="Numero" value={facturaNumero} onChange={(e) => setFacturaNumero(e.target.value)} fullWidth /></Grid>
          <Grid item xs={12} md={2}><TextField label="Remito" value={remito} onChange={(e) => setRemito(e.target.value)} fullWidth /></Grid>
          <Grid item xs={6} md={2}><TextField type="date" label="Fecha" value={fecha} onChange={(e) => setFecha(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Grid>
          <Grid item xs={6} md={2}><TextField type="date" label="Fecha IVA" value={fechaIva} onChange={(e) => setFechaIva(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Grid>
          <Grid item xs={6} md={1.5}><Tooltip title="Importe no gravado"><TextField type="number" label="No grav." value={noGravado} onChange={(e) => setNoGravado(Number(e.target.value))} fullWidth /></Tooltip></Grid>
          <Grid item xs={6} md={1.5}><Tooltip title="Ingresos brutos"><TextField type="number" label="IIBB" value={iibb} onChange={(e) => setIibb(Number(e.target.value))} fullWidth /></Tooltip></Grid>
          <Grid item xs={6} md={1.5}><Tooltip title="Tasa especial municipal"><TextField type="number" label="TEM" value={tem} onChange={(e) => setTem(Number(e.target.value))} fullWidth /></Tooltip></Grid>
          <Grid item xs={6} md={1.5}><Tooltip title="Percepcion de IVA"><TextField type="number" label="Perc. IVA" value={percepcionIva} onChange={(e) => setPercepcionIva(Number(e.target.value))} fullWidth /></Tooltip></Grid>
          <Grid item xs={6} md={1.5}><Tooltip title="Impuestos internos"><TextField type="number" label="Imp. int." value={impuestosInternos} onChange={(e) => setImpuestosInternos(Number(e.target.value))} fullWidth /></Tooltip></Grid>
          <Grid item xs={6} md={1.5}><Tooltip title="Bonificacion o descuento aplicado a la factura"><TextField type="number" label="Bonif." value={bonificacion} onChange={(e) => setBonificacion(Number(e.target.value))} fullWidth /></Tooltip></Grid>
        </Grid>
      </Paper>

      {!idLocal ? <Alert severity="info">Seleccione un local para cargar compras.</Alert> : (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <TextField label="Buscar producto" value={search} onChange={(event) => setSearch(event.target.value)} fullWidth sx={{ mb: 2 }} />
              <DataGridViewport height={520} minWidth={780}>
                <DataGrid rows={productos.map((p) => ({ ...p, id: p.idProducto }))} columns={productColumns} loading={loadingProductos} rowCount={totalProductos} paginationMode="server" paginationModel={paginationModel} onPaginationModelChange={setPaginationModel} pageSizeOptions={[10, 20, 50]} onRowDoubleClick={(params) => agregarProducto(params.row)} />
              </DataGridViewport>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Detalle</Typography>
              <DataGridViewport height={360} minWidth={880}>
                <DataGrid rows={detalle.map((item) => ({ ...item, id: item.idProducto }))} columns={detalleColumns} hideFooter disableRowSelectionOnClick />
              </DataGridViewport>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} md={3}><Typography color="text.secondary">Subtotal</Typography><Typography variant="h6">{money(subtotal)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography color="text.secondary">IVA 21</Typography><Typography variant="h6">{money(iva21)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography color="text.secondary">Lineas</Typography><Typography variant="h6">{money(totalLineas)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography color="text.secondary">Total</Typography><Typography variant="h5">{money(totalFactura)}</Typography></Grid>
                <Grid item xs={12} md={4}><TextField type="date" label="Vencimiento" value={vencimiento} onChange={(e) => setVencimiento(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Grid>
                <Grid item xs={12} md={8}>
                  <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} onClick={guardar} disabled={saving} fullWidth>
                    Guardar compra
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

    </Box>
  );
};

const recalcularLinea = (linea: CompraLinea): CompraLinea => ({
  ...linea,
  cantidad: Number(linea.cantidad || 0),
  ivaCompra: Number(linea.ivaCompra || 0),
  unitarioSinIva: Number(linea.unitarioSinIva || 0),
  unitarioConIva: Number((Number(linea.unitarioSinIva || 0) * (1 + Number(linea.ivaCompra || 0) / 100)).toFixed(2)),
  totalConIva: Number(((Number(linea.cantidad || 0)) * Number((Number(linea.unitarioSinIva || 0) * (1 + Number(linea.ivaCompra || 0) / 100)).toFixed(2))).toFixed(2)),
});

export default CompraCarga;





